import { useEffect } from 'react';
import { useMutation } from 'react-query';
import jwtDecode from 'jwt-decode';
import { Box, Center, HStack, Image, Pressable, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';
import { Linking } from 'react-native';

import { AlertCircleIcon, Modal, Text } from '@pimm/base';
import { IdentityApi, JwtUser } from '@pimm/services/lib/access';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { storeFocusSlice } from '@app/store/slices/storeFocusSlice';
import { storeListSlice } from '@app/store/slices/storeListSlice';
import { userProfileSlice } from '@app/store/slices/userProfileSlice';
import { selectUserProfile } from '@app/store/selectors';
import { FormLogin, useFormLogin } from '@pimm/common';
import { ProcuroTextLogo } from '@app/consts/image-links';
import { RootScreenProps } from '@app/navigations/root';
import { useAppDefaults } from '@app/features/app/hooks';
import { SelectLanguage } from '@app/features/app';
import { Wrapper, useAuthToken } from '@app/features/app';
import { useLiveChat } from '@app/features/live-chat/store/chat.store';
import { NewVersionAvailable } from '@app/components/shared';
import { BrandLogo } from './components/brand-logo';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { PromptConfirmation } from '@app/components/shared/prompt-confirmation';

export type LoginScreenProps = RootScreenProps<'Login'>;

export const LoginScreen = ({ navigation }: LoginScreenProps) => {
  const dispatch = useAppDispatch();
  const { setAuthorization, user } = useAuthToken();
  const { t } = useTranslation();
  const [app, setAppDefaults] = useAppDefaults();
  const modalPrompt = useModalFocus();
  const userProfile = useAppSelector(selectUserProfile);

  // Ensure the support chat widget is always closed on the login screen,
  // regardless of which path (manual logout, session expiry, forced re-login) got us here
  useEffect(() => {
    useLiveChat.getState().reset();
  }, []);

  const form = useFormLogin({
    defaultValues: {
      spid: app?.tenantId,
      username: app?.username,
    },
  });

  const authenticate = useMutation({
    mutationFn: IdentityApi.Authenticate,
    onSuccess: auth => {
      const user: JwtUser = jwtDecode(auth.access_token);

      const allowUser = user.permissions?.includes?.('app.login.enabled') ?? false;

      if (!allowUser) {
        form.setError('root', {
          type: 'access:denied',
        });
        modalPrompt.setOpen();
        return;
      }

      // Clear previous defaults
      if (app?.userId && app.userId !== user?.user_id) {
        setAppDefaults({
          userId: user.user_id,
          username: user.username,
          tenantId: user.tenant_id,
        });
      }
      useLiveChat.getState().reset();
      setAuthorization(auth);

      if (userProfile?.userId && userProfile.userId !== user.user_id) {
        // clear previous logged-in user information
        dispatch(storeListSlice.actions.clear());
        dispatch(storeFocusSlice.actions.clear());
        dispatch(userProfileSlice.actions.clear());
      }

      // @ts-ignore
      navigation.navigate('Main', { screen: 'PositionGuide' });
    },
    onError: (error: { code: number }) => {
      form.setError('root', {
        type: 'invalid_grant',
        message: error.code === 400 ? 'access:invalid_username_password' : 'access:something_went_wrong',
      });
    },
  });

  const handleFormSubmit = () => {
    const { spid, username, password } = form.getValues();
    authenticate.mutate({
      username: `${spid}\\${username}`,
      password: password,
    });
  };

  const handleEmailSupport = () => {
    const formData = form.getValues();
    Linking.openURL(
      `mailto:support@opsbee.ai?subject=${t('app:email_subject')}&body=${t('app:email_body')}%0A%0A${t('access:customer_code')}: ${
        formData.spid
      }%0A${t('access:user')}: ${formData.username}`,
    );
  };

  return (
    <Wrapper>
      <VStack flex={1} alignItems="center" justifyContent="center" w="full" maxH={780}>
        <VStack space={5} w="full" maxW={420}>
          <Box rounded="2xl" bg="white" px={10} pt={7} pb={4} shadow={3}>
            <Center>
              <BrandLogo />
            </Center>

            {/* Login Form */}
            <FormLogin form={form} isLoading={authenticate.isLoading} t={t} onSubmit={handleFormSubmit} />

            <HStack justifyContent="center" textAlign="center" mt={4}>
              <Pressable onPress={() => navigation.replace('RecoverPassword')}>
                <Text size="lg" color="black" fontWeight={600}>
                  {t('access:forgot_password')}
                </Text>
              </Pressable>
            </HStack>

            <Center mt={4}>
              <HStack space={1} alignItems="center" h={10}>
                <Text size="sm" fontWeight={600} color="black">
                  {t('access:powered_by')}
                </Text>
                <Image
                  alt={ProcuroTextLogo.title}
                  source={{
                    uri: ProcuroTextLogo.sourceUri,
                  }}
                  resizeMethod="auto"
                  resizeMode="contain"
                  size="70px"
                />
              </HStack>
              <SelectLanguage />
            </Center>
          </Box>
        </VStack>
      </VStack>

      <NewVersionAvailable />
      <Modal
        size="md"
        _content={{ rounded: 'xl', maxW: { base: '90%', lg: 400 } }}
        isOpen={modalPrompt.isOpen}
        noPadding
        hideClose
        onClose={modalPrompt.setHide}
      >
        <PromptConfirmation
          hideCancel
          _icon={<AlertCircleIcon size={5} color="error.600" />}
          _iconProps={{ colorScheme: 'error' }}
          _confirm={{ text: t('app:email_support') }}
          message={t('app:access_denied_message')}
          title={t('app:access_denied')}
          onClose={modalPrompt.setHide}
          onConfirm={handleEmailSupport}
        />
      </Modal>
    </Wrapper>
  );
};
